// Public "forgot password" page — email in, generic confirmation out.
import React, { useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { KeyRound, Loader2, MailCheck, ArrowLeft } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: email.trim() });
    } catch {
      // always show the generic confirmation — no enumeration
    }
    setSent(true);
    setSending(false);
  };

  return (
    <div className="min-h-screen bg-[#0a0f1a] flex items-center justify-center p-4" data-testid="forgot-password-page">
      <div className="w-full max-w-md bg-white/5 border border-white/10 rounded-2xl p-8">
        {sent ? (
          <div className="text-center" data-testid="forgot-password-sent">
            <div className="w-12 h-12 rounded-2xl bg-teal-500/10 flex items-center justify-center mx-auto mb-4">
              <MailCheck className="w-6 h-6 text-teal-400" />
            </div>
            <h2 className="text-xl font-bold text-white">Check your email</h2>
            <p className="text-white/50 text-sm mt-2">
              If <span className="text-white/80 font-semibold">{email.trim()}</span> has an account,
              a reset link is on its way. It works once and expires in 1 hour.
            </p>
            <Link to="/login" className="inline-flex items-center gap-1.5 mt-6 text-sm text-teal-400 hover:text-teal-300 font-semibold"
              data-testid="forgot-back-to-login">
              <ArrowLeft className="w-4 h-4" /> Back to sign in
            </Link>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-teal-500/10 flex items-center justify-center">
                <KeyRound className="w-5 h-5 text-teal-400" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">Forgot your password?</h2>
                <p className="text-white/40 text-xs mt-0.5">We'll email you a one-time reset link.</p>
              </div>
            </div>
            <form onSubmit={submit} className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-white/50 mb-1.5">Email address</label>
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com" required autoFocus
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-white/20 text-sm focus:outline-none focus:border-teal-500/60 transition-colors"
                  data-testid="forgot-email-input" />
              </div>
              <button type="submit" disabled={sending || !email.trim()}
                className="w-full py-3 bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-400 hover:to-cyan-400 disabled:opacity-40 text-white font-semibold text-sm rounded-xl transition-all flex items-center justify-center gap-2"
                data-testid="forgot-submit-btn">
                {sending && <Loader2 className="w-4 h-4 animate-spin" />}
                {sending ? 'Sending…' : 'Email me a reset link'}
              </button>
            </form>
            <Link to="/login" className="inline-flex items-center gap-1.5 mt-6 text-sm text-white/40 hover:text-white/70"
              data-testid="forgot-cancel-link">
              <ArrowLeft className="w-4 h-4" /> Back to sign in
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
